import * as types from '../mutation-types'
import { Api } from '@/api/api'
import { getFormattedDate } from '@/utils/date'
import { getCheckPriceData } from '@/utils/bookingData'

// initial state
export const initialState = () => {
  return {
    availableCampsites: [],
    availableExtras: [],
    campground: null,
    campsiteTypes: null,
    countries: [],
    currencies: [],
    languages: [],
    sessionTimeout: null,
    status: {
      loading: false,
      success: false,
      error: false,
    },
    urls: null,
    periodNotAvailable: false,
    campingcardAcsiInfo: {},
    availableMandatoryExtras: [],
    hasFetchedAvailabilities: false,
    isPublished: false,
  }
}
let state = initialState()

// getters
const getters = {
  availableCampsites: state => state.availableCampsites,
  availableExtras: state => state.availableExtras,
  campground: state => state.campground,
  campsiteTypes: state => state.campsiteTypes,
  countries: state => state.countries,
  currencies: state => state.currencies,
  languages: state => state.languages,
  sessionTimeout: state => state.sessionTimeout,
  status: state => state.status,
  urls: state => state.urls,
  periodNotAvailable: state => state.periodNotAvailable,
  campingcardAcsiInfo: state => state.campingcardAcsiInfo,
  availableMandatoryExtras: state => state.availableMandatoryExtras,
  hasFetchedAvailabilities: state => state.hasFetchedAvailabilities,
  isPublished: state => state.isPublished,
}

// actions
const actions = {
  getCampgroundData({ dispatch, commit, rootGetters }, lang) {
    // if (state.status.loading) return

    commit(types.CAMPGROUND_STATUS_LOADING, true)
    return Api.getCampgroundData(lang)
      .then(result => {
        /* istanbul ignore else */       
        commit(types.CAMPGROUND_SET_AVAILABLE_EXTRAS, result.campground.extras)
        commit(types.CAMPGROUND_SET_CAMPGROUND, result.campground)
        commit(types.CAMPGROUND_SET_CAMPSITE_TYPES, result.campsite_types)
        commit(types.CAMPGROUND_SET_COUNTRIES, result.countries)
        commit(types.CAMPGROUND_SET_CURRENCIES, result.currencies)
        commit(types.CAMPGROUND_SET_LANGUAGES, result.languages)
        commit(types.CAMPGROUND_SET_URLS, result.urls)
        commit(types.CAMPGROUND_SET_SESSION_TIMEOUT, result.session_timeout)
        commit(
          types.CAMPGROUND_SET_CAMPINGCARD_ACSI_INFO,
          result.campingcard_acsi_info
        )
        commit(types.CAMPGROUND_SET_AVAILABLE_STATUS, result.available)        

        // set default currency if no currency is set
        const setCurrency = rootGetters['application/currency']
        /* istanbul ignore else */
        if (Object.keys(setCurrency).length === 0) {
          const currency = result.currencies.find(
            cur => cur.code === result.campground.currency
          )
          //commit(`application/${types.APP_SET_CURRENCY}`, currency, { root: true })
          dispatch('application/setCurrency', currency, { root: true })
        }

        // set default language
        const langSlug = rootGetters['application/langSlug']
        const language = result.languages.find(cur => cur.code === langSlug)
        dispatch('application/setLanguage', language, { root: true })

        // status handling
        commit(types.CAMPGROUND_STATUS_ERROR, false)
        commit(types.CAMPGROUND_STATUS_LOADING, false)
        commit(types.CAMPGROUND_STATUS_SUCCESS, true)
        return result
      })
      .catch(e => {
        commit(types.CAMPGROUND_STATUS_ERROR, true)
        commit(types.CAMPGROUND_STATUS_LOADING, false)
        commit(types.CAMPGROUND_STATUS_SUCCESS, false)
        throw e
      })
  },
  getExtraPrices({ commit, rootGetters }, value) {
    const user_currency = value ? value : rootGetters['application/currency']
    const urls = rootGetters['campground/urls']
    const discount = rootGetters['application/discountValue']
    const dates = rootGetters['booking/dates']
    const selectedBookingType = rootGetters['booking/selectedBookingType']
    const selectedPersons = rootGetters['booking/selectedPersons'];
    const selectedCampsite = rootGetters['booking/selectedCampsite'];

    const dateFrom = getFormattedDate(dates[0], 'yyyy-MM-dd')
    const dateUntil = getFormattedDate(dates[1], 'yyyy-MM-dd')

    let humanCount = 0;
    selectedPersons.forEach(selectedPerson => {
      const person = rootGetters['campground/campground'].persons.find(
        campgroundPerson => campgroundPerson.id === selectedPerson.id
      );
      if (person.person_type !== 'pet') {
        humanCount += selectedPerson.amount;
      }
    });

    const campsiteId = selectedCampsite && selectedCampsite.id ? selectedCampsite.id : null;
    const queryParams = new URLSearchParams();

    if (user_currency.code) queryParams.set('currency_alt', user_currency.code);
    if (discount) queryParams.set('discount', discount);
    if (dateFrom) queryParams.set('date_from', dateFrom);
    if (dateUntil) queryParams.set('date_until', dateUntil);
    if (selectedBookingType) queryParams.set('choice', selectedBookingType);
    if (humanCount) queryParams.set('persons', humanCount);
    if (campsiteId) queryParams.set('campsite_id', campsiteId);
    
    const url = `${urls.get_prices}?${queryParams.toString()}`;
    return Api.getData(url)
      .then(result => {
        let extras = result.campground.extras
        let mandatoryExtras = result.campground.extras_mandatory 
        // in case of currency alt, map currency_alt values to extra values
        if (result.currency_alt) {
          extras = result.campground.extras.map(extra => {
            extra.currency_alt = result.currency_alt.campground.extras.find(
              ex => ex.id === extra.id
            )
            return extra
          })
        }
        if (result.currency_alt) {
          mandatoryExtras = result.campground.extras_mandatory.map(extra => {
            extra.currency_alt = result.currency_alt.campground.extras_mandatory.find(
              ex => ex.id === extra.id
            )
            return extra
          })
        }
        commit(types.CAMPGROUND_SET_AVAILABLE_EXTRAS, extras)
        commit(types.CAMPGROUND_SET_MANDATORY_EXTRAS, mandatoryExtras)

        // status handling
        commit(types.CAMPGROUND_STATUS_ERROR, false)
        commit(types.CAMPGROUND_STATUS_LOADING, false)
        commit(types.CAMPGROUND_STATUS_SUCCESS, true)
        return result
      })
      .catch(e => {
        commit(types.CAMPGROUND_STATUS_ERROR, true)
        commit(types.CAMPGROUND_STATUS_LOADING, false)
        commit(types.CAMPGROUND_STATUS_SUCCESS, false)
        throw e
      })
  },
  getAvailabilities({ commit, dispatch, rootGetters }, cu) {
    commit(types.CAMPGROUND_SET_HAS_FETCHED_AVAILABILITIES, true)
    // get current chosen persons
    const persons = rootGetters['booking/selectedPersons']
    if (persons.length <= 0) return

    // get urls
    const availabilityUrl = rootGetters['campground/urls'].availability_check

    // get current chosen currency
    const currencyCode = cu ? cu.code : rootGetters['application/currency'].code

    // get current chosen dates
    const dates = rootGetters['booking/dates']
    const dateFrom = getFormattedDate(dates[0], 'yyyy-MM-dd')
    const dateUntil = getFormattedDate(dates[1], 'yyyy-MM-dd')

    // should return in case of null or undefined
    if (dateFrom == null || dateUntil == null) {
      return
    }

    // get current chosen campsite
    const selectedCampsite = rootGetters['booking/selectedCampsite']

    // get checkout data
    const checkout = rootGetters['booking/checkout']
    const booking_id = checkout.responseCheckout.booking_id

    const discount = rootGetters['application/discountValue']
    const baseUrl = `${availabilityUrl}?date_from=${dateFrom}&date_until=${dateUntil}&get_priceinfo=1&currency_alt=${currencyCode}${
      discount ? `&discount=${discount}` : ''
    }`

    const requestUrl = checkout.responseCheckout.booking_id
      ? `${baseUrl}&booking_id=${booking_id}`
      : baseUrl

    commit(types.CAMPGROUND_STATUS_LOADING, true)

    return Api.postData(requestUrl, { persons: persons })
      .then(result => {
        // console.log('results:\n', requestUrl, '\n', result)
        let availableCampsites = result.campsites

        const keyHelper = { number: 2, key: 'selectedCampsiteIsAvailable' }

        // if user has selected campsites
        if (selectedCampsite) {
          const filteredNotAvailable = result.campsites.filter(
            campsite => campsite.amount === 0
          )
          const selectedNotAvailable = filteredNotAvailable.some(
            campsite => campsite.id === selectedCampsite.id
          )

          // if selected campsite is not available anymore
          if (selectedNotAvailable) {
            // this will show modal that selected campsite is not available and deletes selected from store
            dispatch(
              'application/setStepStatus',
              { ...keyHelper, value: false },
              { root: true }
            )
            dispatch('booking/setSelectedCampsite', null, { root: true })
          } else {
            dispatch(
              'application/setStepStatus',
              { ...keyHelper, value: true },
              { root: true }
            )
          }
        } else {
          dispatch(
            'application/setStepStatus',
            { ...keyHelper, value: true },
            { root: true }
          )
        }

        /* sort availableCampsites by amount see: https://developer.mozilla.org/de/docs/Web/JavaScript/Reference/Global_Objects/Array/sort */
        availableCampsites.sort((a, b) => {
          if (a.amount < b.amount) return 1
          if (a.amount > b.amount) return -1
          return 0
        })

        // commit available campsites
        commit(types.CAMPGROUND_SET_AVAILABLE_CAMPSITES, availableCampsites)

        // status handling
        commit(types.CAMPGROUND_STATUS_ERROR, false)
        commit(types.CAMPGROUND_STATUS_LOADING, false)
        commit(types.CAMPGROUND_STATUS_SUCCESS, true)
        return result
      })
      .catch(e => {
        commit(types.CAMPGROUND_STATUS_ERROR, true)
        commit(types.CAMPGROUND_STATUS_LOADING, false)
        commit(types.CAMPGROUND_STATUS_SUCCESS, false)
        commit(types.PERIOD_NOT_AVAILABLE, true)
        commit(types.CAMPGROUND_SET_HAS_FETCHED_AVAILABILITIES, false)
        throw e
      })
  },
  checkPrice({ commit, dispatch, rootGetters }, cu) {
    const campsite = rootGetters['booking/selectedCampsite']
    const availableCampsites = rootGetters['campground/availableCampsites']
    const currency = cu ? cu : rootGetters['application/currency']
    const dates = rootGetters['booking/dates']
    const selectedExtras = rootGetters['booking/selectedExtras']
    const selectedPersons = rootGetters['booking/selectedPersons']
    const selectedBookingType = rootGetters['booking/selectedBookingType']
    const urls = rootGetters['campground/urls']

    const selectedCampsite =
      campsite && campsite.id
        ? availableCampsites.find(x => x.id === campsite.id)
        : campsite

    if (
      dates.length === 2 &&
      selectedPersons.length > 0 &&
      !!campsite &&
      !!selectedCampsite
    ) {
      const booking_data = getCheckPriceData({
        dates,
        selectedCampsite: selectedCampsite,
        selectedExtras,
        selectedPersons,
      })

      // get checkout data
      const checkout = rootGetters['booking/checkout']
      const booking_id = checkout.responseCheckout.booking_id
      const discount = rootGetters['application/discountValue']
      const baseUrl = `${urls.price_check}?&get_priceinfo=1&currency_alt=${
        currency.code
      }${discount ? `&discount=${discount}` : ''}${
        selectedBookingType ? `&choice=${selectedBookingType}` : ''
      }`

      const requestUrl = checkout.responseCheckout.booking_id
        ? `${baseUrl}&booking_id=${booking_id}`
        : baseUrl

      return Api.postData(requestUrl, { booking_data })
        .then(result => {
          if (!result.error) {
            dispatch('booking/setPrice', result, { root: true })
            // handle cancellation terms and save them
            if (result.components.length > 0) {
              let polices = result.components[0].summary

              dispatch('booking/setPolicy', polices, {
                root: true,
              })
            }
            if (result.extras && result.extras.length > 0) {
              let extraStock = result.extras
              dispatch('booking/setExtraStock', extraStock, {
                root: true,
              })
            }
          } else {
            dispatch(
              'booking/setPrice',
              { subtotal: 0, total: 0, currency_alt: { total: 0 } },
              { root: true }
            )
          }
          return result
        })
        .catch(e => {
          commit(types.CAMPGROUND_STATUS_ERROR, true)
          commit(types.CAMPGROUND_STATUS_LOADING, false)
          commit(types.CAMPGROUND_STATUS_SUCCESS, false)
          throw e
        })
    } else {
      dispatch(
        'booking/setPrice',
        { subtotal: 0, total: 0, currency_alt: { total: 0 } },
        { root: true }
      )
    }
  },
  resetState({ commit }) {
    const initState = initialState()
    commit(types.CAMPGROUND_RESET_STATE, initState)
  },
  updateFetchedAvailabilities({ commit }, value) {
    commit(types.CAMPGROUND_SET_HAS_FETCHED_AVAILABILITIES, value)
  },
}

// mutations
const mutations = {
  [types.CAMPGROUND_RESET_STATE](state, initState) {
    Object.keys(initState).forEach(key => {
      state[key] = initState[key]
    })
  },
  [types.CAMPGROUND_SET_AVAILABLE_CAMPSITES](state, payload) {
    state.availableCampsites = payload
  },
  [types.CAMPGROUND_SET_AVAILABLE_EXTRAS](state, payload) {
    state.availableExtras = payload
  },
  [types.CAMPGROUND_SET_COUNTRIES](state, payload) {
    state.countries = []
    Object.entries(payload).forEach(([key, value]) => {
      state.countries.push({ code: key, label: value })
    })
  },
  [types.CAMPGROUND_SET_CAMPGROUND](state, payload) {
    state.campground = payload
  },
  [types.CAMPGROUND_SET_CAMPSITE_TYPES](state, payload) {
    state.campsiteTypes = payload
  },
  [types.CAMPGROUND_SET_CURRENCIES](state, payload) {
    state.currencies = payload
  },
  [types.CAMPGROUND_SET_LANGUAGES](state, payload) {
    state.languages = payload
  },
  [types.CAMPGROUND_SET_URLS](state, payload) {
    state.urls = payload
  },
  [types.CAMPGROUND_SET_SESSION_TIMEOUT](state, payload) {
    state.sessionTimeout = payload
  },
  [types.CAMPGROUND_STATUS_LOADING](state, payload) {
    state.status.loading = payload
  },
  [types.CAMPGROUND_STATUS_SUCCESS](state, payload) {
    state.status.success = payload
  },
  [types.CAMPGROUND_STATUS_ERROR](state, payload) {
    state.status.error = payload
  },
  [types.PERIOD_NOT_AVAILABLE](state, payload) {
    state.periodNotAvailable = payload
  },
  [types.CAMPGROUND_SET_CAMPINGCARD_ACSI_INFO](state, payload) {
    state.campingcardAcsiInfo = payload
  },
  [types.CAMPGROUND_SET_MANDATORY_EXTRAS](state, payload) {
    state.availableMandatoryExtras = payload
  },
  [types.CAMPGROUND_SET_HAS_FETCHED_AVAILABILITIES](state, value) {
    state.hasFetchedAvailabilities = value
  },
  [types.CAMPGROUND_SET_AVAILABLE_STATUS](state, value) {
    state.isPublished = value
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
